package com.tovtalk.data.repository

import com.tovtalk.data.mapper.ChatMapperJs
import com.tovtalk.data.remote.ApiClient
import com.tovtalk.data.remote.ApiEndpoints
import com.tovtalk.data.remote.firebase.JsFirebaseAuthManager
import io.ktor.client.call.body
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.http.content.TextContent
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import com.tovtalk.data.remote.firebase.addDoc
import com.tovtalk.data.remote.firebase.collection
import com.tovtalk.data.remote.firebase.doc
import com.tovtalk.data.remote.firebase.getDoc
import com.tovtalk.data.remote.firebase.getDocs
import com.tovtalk.data.remote.firebase.getFirestore
import com.tovtalk.data.remote.firebase.increment
import com.tovtalk.data.remote.firebase.onSnapshot
import com.tovtalk.data.remote.firebase.orderBy
import com.tovtalk.data.remote.firebase.query
import com.tovtalk.data.remote.firebase.serverTimestamp
import com.tovtalk.data.remote.firebase.setDoc
import com.tovtalk.data.remote.firebase.updateDoc
import com.tovtalk.data.remote.firebase.where
import com.tovtalk.domain.entity.ChatThread
import com.tovtalk.domain.entity.ChatType
import com.tovtalk.domain.entity.Message
import com.tovtalk.domain.entity.PostType
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.repository.ChatRepository
import com.tovtalk.domain.repository.UserRepository
import kotlinx.coroutines.channels.awaitClose
import kotlin.time.Clock
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOf
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.coroutines.suspendCoroutine

private const val COL_USERS = "users"
private const val COL_USER_LIST = "userList"
private const val COL_MESSAGES = "messages"

/**
 * Web ChatRepository using Firebase Firestore — syncs with Android and other devices.
 * Notifications are sent via our backend (POST /chat/notify-*) instead of Firebase Cloud Functions.
 */
class JsChatRepositoryFirestore(
    private val authManager: JsFirebaseAuthManager,
    private val userRepository: UserRepository,
    private val apiClient: ApiClient
) : ChatRepository {

    private val db: dynamic
        get() {
            val app = authManager.getApp()
            return if (app != null) getFirestore(app) else null
        }

    private suspend fun awaitPromise(promise: dynamic): dynamic = suspendCoroutine { cont ->
        val onFulfilled: (dynamic) -> Unit = { cont.resume(it) }
        val onRejected: (dynamic) -> Unit = { cont.resumeWithException(Throwable(it?.toString() ?: "Unknown")) }
        js("(function(p, ok, err) { p.then(ok, err); })").unsafeCast<(dynamic, (dynamic) -> Unit, (dynamic) -> Unit) -> Unit>()(promise, onFulfilled, onRejected)
    }

    private fun getConversationId(id1: Int, id2: Int): String =
        if (id1 < id2) "${id1}_$id2" else "${id2}_$id1"

    private suspend fun syncCurrentUserMapping(user: User) {
        val firebaseUid = authManager.getCurrentUserId() ?: return
        val ref = doc(collection(db, COL_USERS), user.id.toString())
        val data = js("(function(uid){ return { firebase_uid: uid }; })").unsafeCast<(String) -> dynamic>()(firebaseUid)
        try {
            awaitPromise(setDoc(ref, data, js("({ merge: true })")))
        } catch (_: Throwable) { }
    }

    override fun getChatThreads(): Flow<List<ChatThread>> = callbackFlow {
        val database = db ?: run {
            trySend(emptyList())
            close()
            return@callbackFlow
        }
        val currentUser = userRepository.getCurrentUser()
        if (currentUser !is DomainResult.Success) {
            trySend(emptyList())
            close()
            return@callbackFlow
        }
        syncCurrentUserMapping(currentUser.data)
        val userId = currentUser.data.id
        val threadsRef = collection(doc(collection(database, COL_USER_LIST), userId.toString()), "threads")
        val q = query(threadsRef, where("chatType", "==", "approved"), orderBy("updatedAt", "desc"))
        val producerScope = this
        val unsub = onSnapshot(q, { snapshot ->
            producerScope.launch {
                val pairs = js("(function(s){ var d = s&&s.docs||[]; return d.map(function(x){ return { id: x.id, data: typeof x.data==='function'?x.data():x.data }; }); })").unsafeCast<(dynamic) -> Array<dynamic>>()(snapshot)
                val threads = mutableListOf<ChatThread>()
                for (i in 0 until pairs.size) {
                    val p = pairs[i]
                    val otherIdStr = js("(function(p){ return p.id; })").unsafeCast<(dynamic) -> String>()(p)
                    val data = js("(function(p){ return p.data; })").unsafeCast<(dynamic) -> dynamic>()(p)
                    val otherUserId = otherIdStr.toIntOrNull() ?: continue
                    val userResult = userRepository.getUserDetails(otherUserId)
                    val otherUser = (userResult as? DomainResult.Success)?.data ?: continue
                    val conversationId = getConversationId(userId, otherUserId)
                    threads.add(ChatMapperJs.mapToChatThread(otherIdStr, data, otherUser, conversationId))
                }
                trySend(threads)
            }
        }, { err ->
            close(err as? Throwable ?: Throwable("Firestore error"))
        })
        awaitClose { unsub() }
    }

    override fun getChatRequests(): Flow<List<ChatThread>> = callbackFlow {
        val database = db ?: run {
            trySend(emptyList())
            close()
            return@callbackFlow
        }
        val currentUser = userRepository.getCurrentUser()
        if (currentUser !is DomainResult.Success) {
            trySend(emptyList())
            close()
            return@callbackFlow
        }
        syncCurrentUserMapping(currentUser.data)
        val userId = currentUser.data.id
        val threadsRef = collection(doc(collection(database, COL_USER_LIST), userId.toString()), "threads")
        val q = query(threadsRef, where("chatType", "==", "requested"), orderBy("updatedAt", "desc"))
        val producerScope = this
        val unsub = onSnapshot(q, { snapshot ->
            producerScope.launch {
                val pairs = js("(function(s){ var d = s&&s.docs||[]; return d.map(function(x){ return { id: x.id, data: typeof x.data==='function'?x.data():x.data }; }); })").unsafeCast<(dynamic) -> Array<dynamic>>()(snapshot)
                val threads = mutableListOf<ChatThread>()
                for (i in 0 until pairs.size) {
                    val p = pairs[i]
                    val otherIdStr = js("(function(p){ return p.id; })").unsafeCast<(dynamic) -> String>()(p)
                    val data = js("(function(p){ return p.data; })").unsafeCast<(dynamic) -> dynamic>()(p)
                    val otherUserId = otherIdStr.toIntOrNull() ?: continue
                    val userResult = userRepository.getUserDetails(otherUserId)
                    val otherUser = (userResult as? DomainResult.Success)?.data ?: continue
                    val conversationId = getConversationId(userId, otherUserId)
                    threads.add(ChatMapperJs.mapToChatThread(otherIdStr, data, otherUser, conversationId))
                }
                trySend(threads)
            }
        }, { err ->
            close(err as? Throwable ?: Throwable("Firestore error"))
        })
        awaitClose { unsub() }
    }
    override suspend fun deleteChatThread(conversationId: String): DomainResult<Unit> = DomainResult.Success(Unit)
    override suspend fun acceptChatRequest(conversationId: String): DomainResult<Unit> = DomainResult.Success(Unit)
    override suspend fun declineChatRequest(conversationId: String): DomainResult<Unit> = DomainResult.Success(Unit)

    override fun getMessages(conversationId: String): Flow<List<Message>> = callbackFlow {
        val database = db ?: run {
            trySend(emptyList())
            close()
            return@callbackFlow
        }
        val currentUser = userRepository.getCurrentUser()
        if (currentUser !is DomainResult.Success) {
            trySend(emptyList())
            close()
            return@callbackFlow
        }
        syncCurrentUserMapping(currentUser.data)
        val messagesRef = collection(doc(collection(database, COL_MESSAGES), conversationId), "messages")
        val q = query(messagesRef, orderBy("createdAt", "asc"))
        val unsub = onSnapshot(q, { snapshot ->
            val pairs = js("(function(s){ var d = s&&s.docs||[]; return d.map(function(x){ return { id: x.id, data: typeof x.data==='function'?x.data():x.data }; }); })").unsafeCast<(dynamic) -> Array<dynamic>>()(snapshot)
            val list = mutableListOf<Message>()
            for (i in 0 until pairs.size) {
                val pair = pairs[i]
                val id = js("(function(p){ return p.id; })").unsafeCast<(dynamic) -> String>()(pair)
                val data = js("(function(p){ return p.data; })").unsafeCast<(dynamic) -> dynamic>()(pair)
                if (data != null) list.add(ChatMapperJs.mapToMessage(data, id))
            }
            trySend(list)
        }, { err ->
            close(err as? Throwable ?: Throwable("Firestore error"))
        })
        awaitClose { unsub() }
    }

    override suspend fun sendTextMessage(receiverId: Int, text: String): DomainResult<Message> {
        val database = db ?: return DomainResult.Error("Firebase not configured")
        val currentUser = (userRepository.getCurrentUser() as? DomainResult.Success)?.data ?: return DomainResult.Error("Unauthorized")
        syncCurrentUserMapping(currentUser)
        val conversationId = getConversationId(currentUser.id, receiverId)
        val messageData = js("(function(cid,sid,rid,type,content,createdAt){ return { conversationId: cid, senderId: sid, receiverId: rid, messageType: type, content: content, isDeleted: false, createdAt: createdAt }; })").unsafeCast<(String, Int, Int, String, String, dynamic) -> dynamic>()(conversationId, currentUser.id, receiverId, "text", text, serverTimestamp())
        return try {
            val messagesRef = collection(doc(collection(database, COL_MESSAGES), conversationId), "messages")
            val docRef = awaitPromise(addDoc(messagesRef, messageData))
            val messageId = js("(function(ref){ return ref.id; })").unsafeCast<(dynamic) -> String>()(docRef)
            val newRequestedThread = updateThreadInfo(currentUser, receiverId, conversationId, messageData, messageId)
            if (currentUser.id != receiverId) {
                val notifyBody = buildJsonObject {
                    put("conversation_id", conversationId)
                    put("receiver_id", receiverId)
                    put("content", text)
                }
                val notifyResponse = apiClient.httpClient.post(ApiEndpoints.CHAT_NOTIFY_MESSAGE_SENT) {
                    setBody(TextContent(Json.encodeToString(JsonObject.serializer(), notifyBody), ContentType.Application.Json))
                }
                when (notifyResponse.status) {
                    io.ktor.http.HttpStatusCode.Forbidden -> {
                        val err = notifyResponse.body<com.tovtalk.data.remote.dto.ApiErrorBody>()
                        val code = when {
                            err.code == com.tovtalk.domain.entity.ERROR_CODE_KASHRUT_NOT_ALLOWED -> err.code
                            err.detail == com.tovtalk.domain.entity.ERROR_CODE_USER_DOES_NOT_ACCEPT_MESSAGES -> com.tovtalk.domain.entity.ERROR_CODE_USER_DOES_NOT_ACCEPT_MESSAGES
                            else -> null
                        }
                        return DomainResult.Error(
                            err.detail ?: "Forbidden",
                            null,
                            code
                        )
                    }
                    io.ktor.http.HttpStatusCode.OK -> {
                        if (newRequestedThread) {
                            val requestBody = buildJsonObject { put("receiver_id", receiverId) }
                            apiClient.httpClient.post(ApiEndpoints.CHAT_NOTIFY_CHAT_REQUEST) {
                                setBody(TextContent(Json.encodeToString(JsonObject.serializer(), requestBody), ContentType.Application.Json))
                            }
                        }
                    }
                    else -> kotlin.js.console.error("[Chat] Notify API failed:", notifyResponse.status)
                }
            }
            val sentMessage = Message(
                id = messageId,
                conversationId = conversationId,
                senderId = currentUser.id,
                receiverId = receiverId,
                content = text,
                isDeleted = false,
                createdAt = com.tovtalk.utils.nowInstant(),
                receivedAt = null,
                readAt = null
            )
            DomainResult.Success(sentMessage)
        } catch (e: Throwable) {
            DomainResult.Error(e.message ?: "Failed to send message")
        }
    }

    /** @return true if we created a new receiver thread with chatType=REQUESTED */
    private suspend fun updateThreadInfo(
        sender: User,
        receiverId: Int,
        conversationId: String,
        messageData: dynamic,
        messageId: String
    ): Boolean {
        val timestamp = serverTimestamp()
        val senderThreadData = js("(function(msg,ts){ return { lastMessage: msg, updatedAt: ts, chatType: 'approved', isDeleted: false }; })").unsafeCast<(dynamic, dynamic) -> dynamic>()(messageData, timestamp)
        val senderThreadRef = doc(collection(doc(collection(db, COL_USER_LIST), sender.id.toString()), "threads"), receiverId.toString())
        awaitPromise(setDoc(senderThreadRef, senderThreadData, js("({ merge: true })")))
        if (sender.id == receiverId) return false
        val receiverThreadRef = doc(collection(doc(collection(db, COL_USER_LIST), receiverId.toString()), "threads"), sender.id.toString())
        return try {
            val receiverDoc = awaitPromise(getDoc(receiverThreadRef))
            val exists = js("(function(d){ return d && typeof d.exists === 'function' ? d.exists() : !!d; })").unsafeCast<(dynamic) -> Boolean>()(receiverDoc)
            val currentType = js("(function(d){ try { var data = d && typeof d.data === 'function' ? d.data() : (d && d.data); return data && data.chatType ? data.chatType : null; } catch(e){ return null; } })").unsafeCast<(dynamic) -> String?>()(receiverDoc)
            // Data integrity: only write chatType when we have a definitive value.
            // - New chat (doc absent): REQUESTED.
            // - Existing chat: keep its current chatType; if it's missing, OMIT chatType
            //   (do NOT downgrade an existing/approved chat back to REQUESTED on a partial read).
            val resolvedChatType: String? = if (!exists) ChatType.REQUESTED.value else currentType
            val receiverThreadData = js("(function(msg,ts,inc,chatT){ var o = { lastMessage: msg, updatedAt: ts, isDeleted: false, unreadCount: inc }; if(chatT!=null) o.chatType=chatT; return o; })").unsafeCast<(dynamic, dynamic, dynamic, String?) -> dynamic>()(messageData, timestamp, increment(1), resolvedChatType)
            awaitPromise(setDoc(receiverThreadRef, receiverThreadData, js("({ merge: true })")))
            !exists
        } catch (_: Throwable) {
            false
        }
    }

    override suspend fun markAsRead(conversationId: String): DomainResult<Unit> {
        val currentUser = (userRepository.getCurrentUser() as? DomainResult.Success)?.data ?: return DomainResult.Error("Unauthorized")
        val parts = conversationId.split("_")
        val otherUserId = if (parts[0] == currentUser.id.toString()) parts[1] else parts[0]
        val threadRef = doc(collection(doc(collection(db, COL_USER_LIST), currentUser.id.toString()), "threads"), otherUserId)
        return try {
            awaitPromise(updateDoc(threadRef, js("({ unreadCount: 0 })")))
            val messagesRef = collection(doc(collection(db, COL_MESSAGES), conversationId), "messages")
            val q = query(messagesRef, where("receiverId", "==", currentUser.id))
            val snapshot = awaitPromise(getDocs(q))
            val docs = js("(function(s){ return s && s.docs ? s.docs : []; })").unsafeCast<(dynamic) -> Array<dynamic>>()(snapshot)
            val ts = serverTimestamp()
            for (i in 0 until docs.size) {
                val d = docs[i]
                val dataFn = js("(function(x){ return x && typeof x.data === 'function' ? x.data() : (x && x.data); })").unsafeCast<(dynamic) -> dynamic>()(d)
                val hasReadAt = js("(function(data){ return data && data.readAt != null; })").unsafeCast<(dynamic) -> Boolean>()(dataFn)
                if (hasReadAt) continue
                val ref = js("(function(x){ return x && x.ref; })").unsafeCast<(dynamic) -> dynamic>()(d)
                if (ref != null) awaitPromise(updateDoc(ref, js("(function(t){ return { readAt: t }; })").unsafeCast<(dynamic) -> dynamic>()(ts)))
            }
            DomainResult.Success(Unit)
        } catch (e: Throwable) {
            DomainResult.Error(e.message ?: "Failed to mark as read")
        }
    }

    override suspend fun setTypingState(conversationId: String, isTyping: Boolean): DomainResult<Unit> {
        val currentUser = (userRepository.getCurrentUser() as? DomainResult.Success)?.data ?: return DomainResult.Error("Unauthorized")
        val typingRef = doc(collection(doc(collection(db, COL_MESSAGES), conversationId), "typing"), currentUser.id.toString())
        val data = js("(function(typing,ts){ return { isTyping: typing, updatedAt: ts }; })").unsafeCast<(Boolean, dynamic) -> dynamic>()(isTyping, serverTimestamp())
        return try {
            awaitPromise(setDoc(typingRef, data))
            DomainResult.Success(Unit)
        } catch (e: Throwable) {
            DomainResult.Error(e.message ?: "Failed to set typing")
        }
    }

    override fun observeTypingState(conversationId: String): Flow<Map<Int, Boolean>> = callbackFlow {
        val database = db ?: run {
            trySend(emptyMap())
            close()
            return@callbackFlow
        }
        val typingRef = collection(doc(collection(database, COL_MESSAGES), conversationId), "typing")
        val unsub = onSnapshot(typingRef, { snapshot ->
            val pairs = js("(function(s){ var d = s&&s.docs||[]; return d.map(function(x){ var data = typeof x.data==='function'?x.data():x.data; return { id: x.id, isTyping: data&&data.isTyping }; }); })").unsafeCast<(dynamic) -> Array<dynamic>>()(snapshot)
            val map = mutableMapOf<Int, Boolean>()
            for (i in 0 until pairs.size) {
                val p = pairs[i]
                val userId = js("(function(p){ var id = p.id; return id != null ? parseInt(String(id), 10) : null; })").unsafeCast<(dynamic) -> Int?>()(p)
                val isTyping = js("(function(p){ return !!p.isTyping; })").unsafeCast<(dynamic) -> Boolean>()(p)
                if (userId != null) map[userId] = isTyping
            }
            trySend(map)
        }, { trySend(emptyMap()) })
        awaitClose { unsub() }
    }
}
